import {
  ApartmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentFilled,
  LoadingOutlined,
} from '@ant-design/icons'
import { useAutoDispatch } from '@api/useAutoDispatch'
import { useFormatter } from '@api/useFormatter'
import { CancelRunButton } from '@components/OrderDispatch/OptimizerWizard/components/CancelRunButton'
import { addCommasToNumber, BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useState } from 'react'
import type { ReviewHeaderProps } from '../types'
import { InfoIcon } from '@/components/_SiteWide/InfoIcon'

export function ReviewHeader({ data, resetOptimizer, closeWizard, selectedId, createdBy }: ReviewHeaderProps) {
  const [loading, setLoading] = useState('')
  const formatter = useFormatter()
  const { useRunAccept, useRunReject } = useAutoDispatch()

  const handleAccept = async () => {
    setLoading('accept')
    try {
      await useRunAccept.mutateAsync(selectedId)
    } finally {
      setLoading('')
      closeWizard(true)
    }
  }

  const handleReject = async () => {
    setLoading('reject')
    try {
      await useRunReject.mutateAsync(selectedId)
    } finally {
      setLoading('')
      resetOptimizer(true)
    }
  }

  const depots = data?.depots?.length > 1 ? `${data.depots.length} Depots` : data?.depots?.[0]
  const markets = data?.markets?.length > 1 ? `${data.markets.length} Markets` : data?.depots?.[0]

  const { assigned_orders, total_orders, total_schedules } = data ?? {}
  const { loaded_percentage, driver_utilization_percentage, average_shift_length_hours, scheduled_drivers_count } =
    data?.overall_metrics ?? {}

  if (!data)
    return (
      <Horizontal style={{ background: 'var(--theme-color-2-dim)' }} justifyContent='space-between' verticalCenter>
        <Vertical flex={0.6} alignItems='start' className='p-2 ml-4'>
          <Texto category='h5'>Optimization Results </Texto>
          <Texto>Review your optimization results for publishing to schedules.</Texto>
          <Texto>Select a run.</Texto>
        </Vertical>
      </Horizontal>
    )
  return (
    <Horizontal style={{ background: 'var(--theme-color-2-dim)' }} className='p-3' gap={16} verticalCenter>
      <Vertical flex={0.6} justifyContent='center' className='p-3 '>
        <Texto category='h4' weight='bold'>
          Optimization Results{' '}
        </Texto>
        <Texto>Review your optimization results for publishing to schedules.</Texto>
        <Horizontal className=' mt-2'>
          {markets && (
            <BBDTag className='mr-2' theme1>
              <ApartmentOutlined className='pr-2' />
              {markets}
            </BBDTag>
          )}
          {depots && (
            <BBDTag className='ml-2' theme1>
              <EnvironmentFilled className='pr-2' />
              {depots}
            </BBDTag>
          )}
        </Horizontal>
      </Vertical>

      <Vertical flex={1} justifyContent='center' alignItems='start' gap={8}>
        <BBDTag style={{ background: 'var(--theme-color-2)', color: '#fff' }}>Orders Impact</BBDTag>
        <Horizontal
          alignItems='center'
          flex={1}
          className=' bg-1 p-1  bordered'
          style={{ borderRadius: '5px', flexWrap: 'nowrap', width: '100%' }}
        >
          <Vertical flex={2} justifyContent='center' className='pl-3'>
            <Horizontal alignItems='center'>
              <Texto className='text-truncate' appearance='success' category='h3'>
                {assigned_orders} / {total_orders}
              </Texto>
              <Texto className='text-truncate ml-2' appearance='medium'>
                Orders Assigned
              </Texto>
            </Horizontal>
            <div
              style={{ height: '8px', width: '90%', backgroundColor: 'var(--theme-success)', borderRadius: '5px' }}
            />
          </Vertical>
          <div style={{ backgroundColor: 'var(--gray-200)', width: '2px', height: '60%' }} />
          <Vertical justifyContent='center' flex={1} alignItems='center'>
            <Texto category='label' appearance='medium'>
              Drivers Scheduled
              <InfoIcon className='pl-1' tooltipTitle='Scheduled / Total' />
            </Texto>
            <Texto category='h5'>
              {scheduled_drivers_count}/{total_schedules}
            </Texto>
          </Vertical>
        </Horizontal>
      </Vertical>
      <Vertical flex={0.75} alignItems='start' gap={8}>
        <BBDTag style={{ background: 'var(--theme-color-2)', color: '#fff' }}>Driver Metrics</BBDTag>
        <Horizontal flex={1} className='bg-1 bordered' style={{ borderRadius: '5px', width: '100%' }}>
          <Vertical className='pl-3' flex='initial' justifyContent='center'>
            <Texto className='text-ellipsis' category='label' appearance='medium' align='right'>
              Loaded %
              <InfoIcon className='pl-1' tooltipTitle='Loaded Miles / Total Miles' />
            </Texto>
            <Texto category='h5' style={{ whiteSpace: 'nowrap' }} align='right'>
              {Math.round(loaded_percentage ?? 0)}%
            </Texto>
          </Vertical>
          <Vertical verticalCenter width='auto'>
            <Texto className='text-ellipsis' category='label' appearance='medium' align='right'>
              Avg. Utilization Time
              <InfoIcon className='pl-1' tooltipTitle='Scheduled Hours / Hours Available for scheduled drivers' />
            </Texto>
            <Texto category='h5' align='right' style={{ whiteSpace: 'nowrap' }}>
              {Math.round(driver_utilization_percentage ?? 0)}%
            </Texto>
          </Vertical>
          <Vertical className='pr-3' verticalCenter>
            <Texto className='text-ellipsis' category='label' appearance='medium' align='right'>
              Avg. Shift Length
              <InfoIcon className='pl-1' tooltipTitle='Average length of driver shifts' />
            </Texto>
            <Texto category='h5' appearance='secondary' align='right'>
              {Math.round(average_shift_length_hours ?? 0)}h
            </Texto>
          </Vertical>
        </Horizontal>
      </Vertical>

      <Vertical flex={0.25} alignItems='end' justifyContent='space-evenly' className='p-2 mr-2'>
        {data?.status === 'pending' && (
          <>
            <GraviButton
              icon={loading === 'accept' ? <LoadingOutlined /> : <CheckCircleOutlined />}
              success
              buttonText='ACCEPT'
              style={{ borderRadius: '5px', width: '100px' }}
              onClick={handleAccept}
              disabled={!!loading}
            />
            <GraviButton
              icon={loading === 'reject' ? <LoadingOutlined /> : <CloseCircleOutlined />}
              error
              buttonText='REJECT'
              style={{ borderRadius: '5px', width: '100px' }}
              onClick={handleReject}
              disabled={!!loading}
            />
          </>
        )}
        {data?.status === 'in progress' && (
          <CancelRunButton
            selectedRunId={selectedId ?? ''}
            createdBy={createdBy ?? ''}
            resetOptimizer={resetOptimizer}
          />
        )}
      </Vertical>
    </Horizontal>
  )
}
